import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CropperPosition, Dimensions, ImageCroppedEvent } from 'ngx-image-cropper';
import { PDFDocument } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';

(pdfjsLib as any).GlobalWorkerOptions.workerSrc = '/pdf.worker.js';

interface PdfPageState {
  pageNumber: number;
  originalBase64: string;
  croppedBase64?: string;
  width: number;
  height: number;
  included: boolean;
}

@Component({
  selector: 'app-pdf-crop-dialog',
  templateUrl: './pdf-crop-dialog.component.html',
  styleUrls: ['./pdf-crop-dialog.component.scss'],
})
export class PdfCropDialogComponent implements OnChanges {
  @Input() visible = false;
  @Input() pdfFile?: File;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() croppedFile = new EventEmitter<File>();

  readonly maxSizeMB = 20;
  readonly renderScale = 1.5;

  // select: 先挑要上傳的頁面；crop: 只針對已挑選的頁面逐頁裁切
  step: 'select' | 'crop' = 'select';
  pages: PdfPageState[] = [];
  currentPageIndex = 0;
  isLoading = false;
  isProcessing = false;
  sizeError = false;
  loadError = false;
  noPageSelectedError = false;
  cropperPosition: CropperPosition = { x1: 0, y1: 0, x2: 0, y2: 0 };

  private currentCropEvent?: ImageCroppedEvent;
  private originalFileName = 'document.pdf';

  get currentPage(): PdfPageState | undefined {
    return this.pages[this.currentPageIndex];
  }

  get currentPageImage(): string | undefined {
    return this.currentPage?.croppedBase64 ?? this.currentPage?.originalBase64;
  }

  get hasIncludedPage(): boolean {
    return this.pages.some((p) => p.included);
  }

  get selectedCount(): number {
    return this.pages.filter((p) => p.included).length;
  }

  // 裁切步驟側欄用：只列已勾選的頁面，index 保留它在 pages 裡的原始位置
  get includedPages(): { page: PdfPageState; index: number }[] {
    return this.pages
      .map((page, index) => ({ page, index }))
      .filter((item) => item.page.included);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pdfFile'] && this.pdfFile) {
      this.originalFileName = this.pdfFile.name;
      this.loadPdf(this.pdfFile);
    }
  }

  private async loadPdf(file: File): Promise<void> {
    this.isLoading = true;
    this.loadError = false;
    this.sizeError = false;
    this.noPageSelectedError = false;
    this.pages = [];
    this.step = 'select';
    this.currentPageIndex = 0;
    this.currentCropEvent = undefined;

    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdfDoc = await (pdfjsLib as any).getDocument({ data: arrayBuffer }).promise;
      const pages: PdfPageState[] = [];
      for (let pageNumber = 1; pageNumber <= pdfDoc.numPages; pageNumber++) {
        const rendered = await this.renderPageToBase64(pdfDoc, pageNumber);
        pages.push({ pageNumber, originalBase64: rendered.base64, width: rendered.width, height: rendered.height, included: true });
      }
      this.pages = pages;
      // 只有一頁就沒有「選頁」的必要，直接進入裁切
      if (pages.length === 1) {
        this.step = 'crop';
      }
    } catch {
      this.loadError = true;
    } finally {
      this.isLoading = false;
    }
  }

  private async renderPageToBase64(pdfDoc: any, pageNumber: number): Promise<{ base64: string; width: number; height: number }> {
    const page = await pdfDoc.getPage(pageNumber);
    const viewport = page.getViewport({ scale: this.renderScale });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const context = canvas.getContext('2d');
    await page.render({ canvasContext: context, viewport }).promise;
    return {
      base64: canvas.toDataURL('image/jpeg', 0.92),
      width: viewport.width,
      height: viewport.height,
    };
  }

  goToPage(index: number): void {
    if (index < 0 || index >= this.pages.length || index === this.currentPageIndex) {
      return;
    }
    this.commitCurrentPageCrop();
    this.currentPageIndex = index;
  }

  // 切換這一頁「是否要包含在最終上傳的 PDF 裡」——預設全部頁面都會包含，
  // 使用者可以自己取消不需要的頁，不用每頁都上傳
  togglePageIncluded(index: number): void {
    const page = this.pages[index];
    if (page) {
      page.included = !page.included;
      if (page.included) {
        this.noPageSelectedError = false;
      }
    }
  }

  selectAllPages(): void {
    this.pages.forEach((p) => (p.included = true));
    this.noPageSelectedError = false;
  }

  deselectAllPages(): void {
    this.pages.forEach((p) => (p.included = false));
  }

  nextStep(): void {
    if (!this.hasIncludedPage) {
      this.noPageSelectedError = true;
      return;
    }
    this.noPageSelectedError = false;
    // 目前頁如果沒被勾選（或是第一次進來），改指到第一個已勾選的頁面
    if (!this.currentPage?.included) {
      this.currentPageIndex = this.pages.findIndex((p) => p.included);
    }
    this.currentCropEvent = undefined;
    this.step = 'crop';
  }

  backToSelect(): void {
    this.commitCurrentPageCrop();
    this.step = 'select';
  }

  onImageCropped(event: ImageCroppedEvent): void {
    this.currentCropEvent = event;
  }

  // 保險起見，強制把初始裁切框設成滿版整頁——不要完全依賴 ngx-image-cropper
  // 自己的預設尺寸計算，不同容器尺寸下有可能算出來的框沒有蓋滿整頁。
  onCropperReady(dimensions: Dimensions): void {
    this.cropperPosition = { x1: 0, y1: 0, x2: dimensions.width, y2: dimensions.height };
  }

  resetCurrentPageCrop(): void {
    if (this.currentPage) {
      this.currentPage.croppedBase64 = undefined;
      // 要重新賦值成新的物件參考——ngx-image-cropper 只有在 [cropper] 這個
      // Input 的參考真的改變時（ngOnChanges）才會重新定位裁切框，原地修改沒有用。
      this.cropperPosition = { x1: 0, y1: 0, x2: this.currentPage.width, y2: this.currentPage.height };
    }
    this.currentCropEvent = undefined;
  }

  private commitCurrentPageCrop(): void {
    if (this.currentCropEvent?.base64 && this.currentPage) {
      this.currentPage.croppedBase64 = this.currentCropEvent.base64;
    }
    this.currentCropEvent = undefined;
  }

  async confirm(): Promise<void> {
    this.commitCurrentPageCrop();
    this.sizeError = false;
    this.noPageSelectedError = false;

    const selectedPages = this.pages.filter((p) => p.included);
    if (selectedPages.length === 0) {
      this.noPageSelectedError = true;
      return;
    }

    this.isProcessing = true;

    try {
      const pdfDoc = await PDFDocument.create();
      for (const page of selectedPages) {
        const imageBase64 = page.croppedBase64 ?? page.originalBase64;
        const imageBytes = this.base64ToUint8Array(imageBase64);
        const embeddedImage = await pdfDoc.embedJpg(imageBytes);
        const newPage = pdfDoc.addPage([embeddedImage.width, embeddedImage.height]);
        newPage.drawImage(embeddedImage, {
          x: 0,
          y: 0,
          width: embeddedImage.width,
          height: embeddedImage.height,
        });
      }

      const pdfBytes = await pdfDoc.save();
      const file = new File([pdfBytes.buffer as ArrayBuffer], this.originalFileName, { type: 'application/pdf' });

      if (file.size > this.maxSizeMB * 1024 * 1024) {
        this.sizeError = true;
        this.isProcessing = false;
        return;
      }

      this.croppedFile.emit(file);
      this.isProcessing = false;
      this.close();
    } catch {
      this.isProcessing = false;
      this.loadError = true;
    }
  }

  close(): void {
    this.pages = [];
    this.step = 'select';
    this.currentPageIndex = 0;
    this.currentCropEvent = undefined;
    this.isLoading = false;
    this.isProcessing = false;
    this.sizeError = false;
    this.loadError = false;
    this.noPageSelectedError = false;
    this.visibleChange.emit(false);
  }

  private base64ToUint8Array(dataUrl: string): Uint8Array {
    const base64 = dataUrl.substring(dataUrl.indexOf(',') + 1);
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }
}
